// North Shore service-area pages. Each suburb has a genuinely distinct intro
// (local housing stock / character) so these read as real local pages, not
// find-and-replace doorway pages. CommonJS so both React (import) and the
// Node build script (require, via routeMeta.js) can read it.
//
// slug → URL /service-areas/<slug>; nearby → internal links to adjacent suburbs.

const serviceAreas = [
  {
    slug: 'takapuna',
    name: 'Takapuna',
    blurb:
      "Takapuna mixes beachfront apartments, the busy Hurstmere Road retail strip and commercial offices, so we're called out here for everything from apartment lockouts to shopfront and office door hardware. It's a short hop from our Milford base, and we carry the common residential and commercial locks in the van.",
    nearby: ['milford', 'devonport', 'northcote', 'belmont'],
  },
  {
    slug: 'devonport',
    name: 'Devonport',
    blurb:
      "Devonport's older villas and character homes often run original or worn locks that are better rekeyed or sympathetically upgraded than bluntly replaced. We handle heritage-style hardware as readily as modern deadlocks, and we're a quick trip around the waterfront from the rest of the Shore.",
    nearby: ['takapuna', 'northcote', 'milford', 'bayswater'],
  },
  {
    slug: 'milford',
    name: 'Milford',
    blurb:
      "Milford is our home patch — we're based right here, so response is quick. From beachside homes to the Milford shops, we cover lockouts, rekeying and lock repairs, plus commercial door hardware for local businesses.",
    nearby: ['takapuna', 'mairangi-bay', 'albany', 'castor-bay'],
  },
  {
    slug: 'albany',
    name: 'Albany',
    blurb:
      "Albany's newer subdivisions and townhouses tend to run modern euro-cylinder and smart-lock hardware, while its business parks need commercial door hardware and access control. We cover both — and the area's rapid growth means plenty of new fit-outs, rekeys and lock upgrades.",
    nearby: ['glenfield', 'browns-bay', 'mairangi-bay', 'greenhithe'],
  },
  {
    slug: 'glenfield',
    name: 'Glenfield',
    blurb:
      "Glenfield's mix of family homes and light-industrial units keeps us busy with residential rekeys and lock repairs alongside commercial door hardware for the workshops and units off Wairau Road.",
    nearby: ['birkenhead', 'northcote', 'albany', 'beach-haven'],
  },
  {
    slug: 'browns-bay',
    name: 'Browns Bay',
    blurb:
      "Browns Bay and the East Coast Bays are largely coastal residential, where salt air is hard on older locks. We replace seized and corroded hardware, rekey after house moves, and fit home security upgrades for peace of mind by the sea.",
    nearby: ['mairangi-bay', 'torbay', 'albany', 'rothesay-bay'],
  },
  {
    slug: 'birkenhead',
    name: 'Birkenhead',
    blurb:
      "Birkenhead and Highbury's older homes and hillside streets see plenty of rekeys, lock repairs and home security upgrades. We come to you across the area with the common hardware already on board.",
    nearby: ['northcote', 'glenfield', 'beach-haven'],
  },
  {
    slug: 'northcote',
    name: 'Northcote',
    blurb:
      "Northcote's blend of townhouses, older homes and the town centre means residential lockouts and rekeys plus shopfront and commercial door hardware — all covered by our mobile service.",
    nearby: ['birkenhead', 'takapuna', 'glenfield'],
  },
  {
    slug: 'mairangi-bay',
    name: 'Mairangi Bay',
    blurb:
      "Mairangi Bay and the surrounding bays are coastal residential. We handle lockouts, rekeying and weather-worn lock replacement, and fit home security upgrades for the area's family homes.",
    nearby: ['browns-bay', 'milford', 'torbay', 'campbells-bay'],
  },
  {
    slug: 'torbay',
    name: 'Torbay',
    blurb:
      "Torbay, at the northern end of the Shore, is residential and a little further out. We still come to you for lockouts, rekeys and lock repairs — a quick call ahead helps us plan the run so you're not waiting.",
    nearby: ['browns-bay', 'mairangi-bay', 'long-bay'],
  },
  {
    slug: 'beach-haven',
    name: 'Beach Haven',
    blurb:
      "Beach Haven sits out on its own tidal peninsula — a mix of original ex-state homes and newer infill builds, many close enough to the water that salt air works away at older hardware. We come out here for rekeys after settlement, sticking and seized locks, and home security tidy-ups, with the common locks already on the van.",
    nearby: ['birkenhead', 'glenfield'],
  },
  {
    slug: 'bayswater',
    name: 'Bayswater',
    blurb:
      "Bayswater is a quiet marina peninsula of character bungalows and villas between Belmont and Devonport, where the sea air is hard on older locks. We rekey after house moves, replace weather-worn hardware, and fit window and deadlock upgrades — a short run from our Milford base.",
    nearby: ['belmont', 'devonport', 'takapuna'],
  },
  {
    slug: 'belmont',
    name: 'Belmont',
    blurb:
      "Belmont sits right on Lake Road between Takapuna and Devonport — a settled patch of family homes plus the Belmont shops. We handle lockouts, rekeys after moving in, and lock repairs, along with door hardware for the local shopfronts.",
    nearby: ['takapuna', 'bayswater', 'devonport'],
  },
  {
    slug: 'forrest-hill',
    name: 'Forrest Hill',
    blurb:
      "Forrest Hill is established inland residential between Takapuna and Sunnynook — post-war homes and steady infill, plus the AUT Millennium end. We're called out for rekeys, worn-lock repairs and home security upgrades, and we come to you.",
    nearby: ['takapuna', 'sunnynook', 'milford'],
  },
  {
    slug: 'sunnynook',
    name: 'Sunnynook',
    blurb:
      "Sunnynook is a family suburb tucked just off the Northern Motorway, which makes it a quick response for us. Lockouts, rekeying after a move, lock repairs and home security upgrades — all handled on site from the van.",
    nearby: ['forrest-hill', 'mairangi-bay', 'milford'],
  },
  {
    slug: 'rothesay-bay',
    name: 'Rothesay Bay',
    blurb:
      "Rothesay Bay is coastal East Coast Bays living, where salt air seizes older locks over time. We replace corroded and sticking hardware, rekey after house moves, and fit home security upgrades for the bays' family homes.",
    nearby: ['browns-bay', 'mairangi-bay', 'long-bay'],
  },
  {
    slug: 'long-bay',
    name: 'Long Bay',
    blurb:
      "Long Bay, at the northern end of the Shore by the regional park, is largely newer masterplanned housing running modern euro-cylinder and smart-lock hardware. We cover new-build rekeys, lock upgrades and smart-lock setup — a call ahead helps us plan the run out.",
    nearby: ['torbay', 'browns-bay'],
  },
  {
    slug: 'castor-bay',
    name: 'Castor Bay',
    blurb:
      "Castor Bay's clifftop and beachside homes between Milford and Mairangi Bay take a beating from the coastal weather. We rekey after moving, swap out weather-worn locks and fit home security upgrades, with common hardware on board.",
    nearby: ['milford', 'mairangi-bay', 'forrest-hill'],
  },
  {
    slug: 'campbells-bay',
    name: 'Campbells Bay',
    blurb:
      "Campbells Bay is leafy coastal residential above Kennedy Park in the East Coast Bays. We handle lockouts, rekeys and lock repairs, and fit deadlocks, window locks and smart hardware for the area's family homes.",
    nearby: ['mairangi-bay', 'castor-bay', 'sunnynook'],
  },
  {
    slug: 'greenhithe',
    name: 'Greenhithe',
    blurb:
      "Greenhithe, out on the Upper Harbour, mixes lifestyle blocks and newer subdivisions on larger sections — often with sheds, gates and outbuildings to secure as well as the house. We rekey, upgrade locks and fit home security across the whole property, and cover the local businesses too.",
    nearby: ['albany', 'glenfield'],
  },
];

module.exports = { serviceAreas };
